import React, { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';

export interface UnsavedChangesDialogProps {
  /**
   * Controls whether the dialog is open
   */
  open: boolean;
  /**
   * Callback when the dialog open state changes
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Callback when user confirms they want to discard changes
   */
  onConfirm: () => void;
  /**
   * Optional custom title for the dialog
   */
  title?: ReactNode;
  /**
   * Optional custom description for the dialog
   */
  description?: ReactNode;
  /**
   * Optional custom content for the confirm button
   */
  confirmContent?: ReactNode;
  /**
   * Optional custom content for the cancel button
   */
  cancelContent?: ReactNode;
  /**
   * Where focus goes once the dialog has closed and the user kept the changes;
   * after a discard it stays where the discard put it. Without it, Radix's
   * own close focus applies.
   */
  returnFocus?: () => void;
}

export const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmContent,
  cancelContent,
  returnFocus
}) => {
  const { t } = useTranslation('patterns');
  const keptRef = useRef(true);

  const handleConfirm = () => {
    keptRef.current = false;
    onConfirm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-120"
        closeButton={false}
        onOpenAutoFocus={() => {
          keptRef.current = true;
        }}
        onCloseAutoFocus={
          returnFocus
            ? event => {
                event.preventDefault();
                if (keptRef.current) returnFocus();
              }
            : undefined
        }
      >
        <DialogHeader>
          <DialogTitle>{title ?? t('UnsavedChangesDialog.title')}</DialogTitle>
          <DialogDescription className="px-6 py-4 text-base">
            {description ?? t('UnsavedChangesDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={handleCancel}>
            {cancelContent ?? t('UnsavedChangesDialog.cancel')}
          </Button>
          <Button variant="danger" onClick={handleConfirm}>
            {confirmContent ?? t('UnsavedChangesDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
